import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, ArrowRight, Eye, EyeOff, Check, Loader2, AlertCircle,
} from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const PLANS = [
  { id: 'essential',    name: 'Essential',    price: '£49'  },
  { id: 'professional', name: 'Professional', price: '£99',  recommended: true },
  { id: 'business',     name: 'Business',     price: '£199' },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const sanitiseDomain = (raw) =>
  (raw || '')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .trim()
    .toLowerCase();

const isValidDomain = (raw) => {
  const v = sanitiseDomain(raw);
  return /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(v);
};

/* ── Shared card chrome ── */
const cardStyle = {
  position: 'relative',
  background: 'rgba(10,10,18,0.88)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--b1)',
  borderRadius: 16,
  padding: '2.5rem',
  width: '100%',
  maxWidth: 420,
  boxShadow: '0 0 60px rgba(139,92,246,0.10)',
};

const inputStyle = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

const primaryButtonStyle = {
  background: 'var(--a)',
  color: '#fff',
  width: '100%',
  height: 44,
  borderRadius: 3,
  fontFamily: 'DM Sans, sans-serif',
  fontWeight: 500,
};

const STATES = {
  EMAIL: 'email',
  SIGNUP: 'signup',
  SIGNIN: 'signin',
  WELCOME: 'welcome',
};

export default function AuthPage() {
  useSEO({
    title: 'Get Started | Acodia — Grow. Defend. Be Found.',
    description: 'Sign in or start your Acodia Watch free trial in seconds.',
    path: '/auth',
    robots: 'noindex,nofollow',
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');
  const initialPlan = ['essential', 'professional', 'business'].includes(planFromUrl)
    ? planFromUrl
    : 'professional';
  const { login, tokens, user, saveTokens, fetchUser, authFetch } = useAuth();

  // When a visitor arrives from a "Choose plan" CTA
  // (?next=/watch&plan=X&interval=Y), send them straight to Stripe
  // Checkout after auth completes instead of the normal dashboard landing.
  const nextFromUrl = searchParams.get('next');
  const intervalFromUrl = (searchParams.get('interval') || '').toLowerCase();
  const wantsCheckout =
    nextFromUrl === '/watch' &&
    ['essential', 'professional', 'business'].includes(planFromUrl) &&
    ['monthly', 'annual'].includes(intervalFromUrl);

  const proceedAfterAuth = useCallback(async () => {
    if (wantsCheckout) {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan: planFromUrl, interval: intervalFromUrl }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
      } catch {
        /* fall through to dashboard */
      }
    }
    navigate('/dashboard?welcome=true', { replace: true });
  }, [wantsCheckout, authFetch, planFromUrl, intervalFromUrl, navigate]);

  const [state, setState] = useState(STATES.EMAIL);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  // If a session is already live, jump straight to the welcome screen.
  useEffect(() => {
    if (tokens && user && state === STATES.EMAIL) {
      setState(STATES.WELCOME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailContinue = async (e) => {
    e?.preventDefault?.();
    const v = email.trim().toLowerCase();
    if (!v || !EMAIL_RE.test(v)) {
      setError('Please enter a valid email');
      return;
    }
    setError('');
    setChecking(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/auth/check-email?email=${encodeURIComponent(v)}`
      );
      const data = await res.json().catch(() => ({}));
      setEmail(v);
      setState(data.exists ? STATES.SIGNIN : STATES.SIGNUP);
    } catch (err) {
      setError('Could not check email — please try again');
    } finally {
      setChecking(false);
    }
  };

  const goToWelcome = useCallback(() => setState(STATES.WELCOME), []);

  return (
    <div
      style={{
        background: 'var(--bg)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        color: 'var(--white)',
      }}
    >
      {state === STATES.EMAIL && (
        <EmailEntryCard
          email={email}
          setEmail={setEmail}
          error={error}
          checking={checking}
          onSubmit={handleEmailContinue}
        />
      )}
      {state === STATES.SIGNUP && (
        <SignupCard
          email={email}
          initialPlan={initialPlan}
          onBack={() => setState(STATES.EMAIL)}
          onSuccess={proceedAfterAuth}
          saveTokens={saveTokens}
          fetchUser={fetchUser}
        />
      )}
      {state === STATES.SIGNIN && (
        <SignInCard
          email={email}
          onBack={() => setState(STATES.EMAIL)}
          onSuccess={wantsCheckout ? proceedAfterAuth : goToWelcome}
          login={login}
        />
      )}
      {state === STATES.WELCOME && (
        <WelcomeBackCard
          authFetch={authFetch}
          user={user}
          onContinue={() => navigate('/dashboard', { replace: true })}
        />
      )}
    </div>
  );
}

/* ───────────────────── STATE 1 — Email entry ───────────────────── */

function AcodiaLogo() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
      <img
        src="/images/logo/logo-nav-64.png"
        alt="Acodia"
        style={{ height: 44, width: 'auto', display: 'block' }}
      />
    </div>
  );
}

function EmailEntryCard({ email, setEmail, error, checking, onSubmit }) {
  const [toast, setToast] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleGoogle = () => {
    setToast('Coming soon — Google sign-in is on the way.');
    setTimeout(() => setToast(''), 2400);
  };

  return (
    <div data-testid="auth-email-entry" style={cardStyle}>
      <AcodiaLogo />

      <h1 style={{
        fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.4rem',
        color: 'var(--white)', textAlign: 'center', lineHeight: 1.25,
      }}>
        Welcome to Acodia
      </h1>
      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
        color: 'var(--ts)', textAlign: 'center', marginTop: '0.35rem',
      }}>
        Enter your email to get started
      </p>

      <form onSubmit={onSubmit} style={{ marginTop: '1.5rem' }}>
        <Input
          ref={inputRef}
          data-testid="auth-email-input"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="font-body text-sm rounded-[3px]"
          style={{ ...inputStyle, height: 44 }}
          autoFocus
        />
        {error && (
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
            color: 'var(--danger)', marginTop: '0.5rem',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}>
            <AlertCircle size={13} /> {error}
          </p>
        )}
        <Button
          type="submit"
          data-testid="auth-email-continue"
          disabled={checking}
          className="btn-shimmer"
          style={{ ...primaryButtonStyle, marginTop: '0.75rem', opacity: checking ? 0.7 : 1 }}
        >
          {checking ? (
            <span className="inline-flex items-center" style={{ gap: '0.5rem' }}>
              <Loader2 size={14} style={{ animation: 'scannerSpin 1s linear infinite' }} />
              Checking...
            </span>
          ) : (
            <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
              Continue <ArrowRight size={14} />
            </span>
          )}
        </Button>
      </form>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1.25rem 0' }}>
        <div style={{ flex: 1, height: 1, background: 'var(--b1)' }} />
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: 'var(--ts)' }}>or</span>
        <div style={{ flex: 1, height: 1, background: 'var(--b1)' }} />
      </div>

      <Button
        type="button"
        data-testid="auth-google-button"
        onClick={handleGoogle}
        className="inline-flex items-center justify-center"
        style={{
          width: '100%', height: 44, borderRadius: 3,
          background: 'transparent', border: '1px solid var(--b1)',
          color: 'var(--white)', fontFamily: 'DM Sans, sans-serif', gap: '0.6rem',
        }}
      >
        <GoogleGIcon /> Continue with Google
      </Button>

      {toast && (
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
          color: 'var(--ts)', textAlign: 'center', marginTop: '0.6rem',
        }}>
          {toast}
        </p>
      )}

      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem',
        color: 'var(--ts)', textAlign: 'center', marginTop: '1.25rem',
        lineHeight: 1.55,
      }}>
        By continuing you agree to our{' '}
        <Link to="/terms" style={{ color: 'var(--a)' }}>Terms of Service</Link>{' '}and{' '}
        <Link to="/privacy" style={{ color: 'var(--a)' }}>Privacy Policy</Link>.
      </p>
    </div>
  );
}

function GoogleGIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C40.9 36.4 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

/* ───────────────────── STATE 2 — Signup ───────────────────── */

function SignupCard({ email, initialPlan = 'professional', onBack, onSuccess, saveTokens, fetchUser }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [domain, setDomain] = useState('');
  const [plan, setPlan] = useState(initialPlan);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!isValidDomain(domain)) {
      setError('Please enter a valid domain, e.g. yourbusiness.co.uk');
      return;
    }

    const cleanDomain = sanitiseDomain(domain);
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/active-guard/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan,
          domains: [cleanDomain],
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.toLowerCase(),
          password,
          confirm_password: password,
          // Backend requires a company name; default to the domain so the
          // user doesn't have to type the same thing twice.
          company_name: cleanDomain,
          authorised: true,
          honeypot: '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data?.detail;
        const msg = typeof d === 'string'
          ? d
          : (d?.error || `Signup failed (${res.status})`);
        setError(msg);
        return;
      }
      if (data?.tokens && saveTokens) {
        saveTokens(data.tokens);
        try { await fetchUser?.(); } catch { /* non-fatal */ }
      }
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="auth-signup" style={cardStyle}>
      <BackButton onClick={onBack} />
      <AcodiaLogo />

      <h1 style={{
        fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.3rem',
        color: 'var(--white)', textAlign: 'center',
      }}>
        Start your free trial
      </h1>

      <div style={{
        background: 'rgba(139,92,246,0.08)',
        border: '1px solid rgba(139,92,246,0.2)',
        borderRadius: 10, padding: '1rem',
        marginTop: '1.25rem', marginBottom: '1.5rem',
      }}>
        {['7 days completely free', 'No card required to start', 'Full access to all features', 'Cancel any time'].map((b) => (
          <div key={b} style={{
            display: 'flex', alignItems: 'center', gap: '0.55rem',
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem',
            color: 'var(--tm)', padding: '0.25rem 0',
          }}>
            <Check size={14} style={{ color: 'var(--ok)', flexShrink: 0 }} />
            <span>{b}</span>
          </div>
        ))}
      </div>

      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem',
        color: 'var(--ts)', marginBottom: '1rem',
      }}>
        Signing up as <span style={{ color: 'var(--white)' }}>{email}</span>
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Input
            data-testid="auth-signup-firstname"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42, flex: 1 }}
          />
          <Input
            data-testid="auth-signup-lastname"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42, flex: 1 }}
          />
        </div>

        <div style={{ marginTop: '0.5rem', position: 'relative' }}>
          <Input
            data-testid="auth-signup-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password (8+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42, paddingRight: 38 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 10, top: 0, bottom: 0,
              display: 'flex', alignItems: 'center',
              background: 'none', border: 'none', color: 'var(--ts)', cursor: 'pointer',
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <div style={{ marginTop: '0.5rem' }}>
          <Input
            data-testid="auth-signup-domain"
            placeholder="yourbusiness.co.uk"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42 }}
          />
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem',
            color: 'var(--ts)', marginTop: '0.3rem',
          }}>
            Your website to monitor
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.45rem', marginTop: '1rem' }}>
          {PLANS.map((p) => {
            const selected = p.id === plan;
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`auth-signup-plan-${p.id}`}
                onClick={() => setPlan(p.id)}
                style={{
                  flex: 1, padding: '0.75rem',
                  background: selected ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${selected ? 'var(--a)' : 'var(--b1)'}`,
                  borderRadius: 8, cursor: 'pointer',
                  textAlign: 'center', position: 'relative',
                  fontFamily: 'DM Sans, sans-serif',
                  color: 'var(--white)',
                }}
              >
                {p.recommended && (
                  <span style={{
                    position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--a)', color: '#fff',
                    fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4,
                    letterSpacing: '0.05em',
                  }}>
                    ★ POPULAR
                  </span>
                )}
                <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, marginTop: 2 }}>{p.price}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--ts)' }}>/month</div>
              </button>
            );
          })}
        </div>

        {error && (
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
            color: 'var(--danger)', marginTop: '0.75rem',
            display: 'flex', alignItems: 'flex-start', gap: '0.35rem',
          }}>
            <AlertCircle size={13} style={{ marginTop: 2 }} /> {error}
          </p>
        )}

        <label
          htmlFor="auth-signup-terms"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.55rem',
            marginTop: '1rem', cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
            color: 'var(--ts)', lineHeight: 1.5,
          }}
        >
          <input
            id="auth-signup-terms"
            type="checkbox"
            data-testid="auth-signup-terms"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            style={{ marginTop: 2, accentColor: 'var(--a)', width: 16, height: 16, flexShrink: 0 }}
          />
          <span>
            I agree to the{' '}
            <Link to="/terms" style={{ color: 'var(--a)' }}>Terms of Service</Link>{' '}and{' '}
            <Link to="/privacy" style={{ color: 'var(--a)' }}>Privacy Policy</Link>.
          </span>
        </label>

        <Button
          type="submit"
          data-testid="auth-signup-submit"
          disabled={submitting || !agreedToTerms}
          className="btn-shimmer"
          style={{ ...primaryButtonStyle, marginTop: '1rem', opacity: (submitting || !agreedToTerms) ? 0.6 : 1 }}
        >
          {submitting ? (
            <span className="inline-flex items-center" style={{ gap: '0.5rem' }}>
              <Loader2 size={14} style={{ animation: 'scannerSpin 1s linear infinite' }} />
              Starting your trial...
            </span>
          ) : (
            <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
              Start Free Trial <ArrowRight size={14} />
            </span>
          )}
        </Button>
      </form>
    </div>
  );
}

/* ───────────────────── STATE 3 — Sign in ───────────────────── */

function SignInCard({ email, onBack, onSuccess, login }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const passwordRef = useRef(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(email, password, totpRequired ? totpCode : undefined);
      if (result?.requires_totp) {
        setTotpRequired(true);
        return;
      }
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Sign in failed — check your password and try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="auth-signin" style={cardStyle}>
      <BackButton onClick={onBack} />
      <AcodiaLogo />

      <h1 style={{
        fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.3rem',
        color: 'var(--white)', textAlign: 'center',
      }}>
        Welcome back
      </h1>

      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
        color: 'var(--ts)', textAlign: 'center',
        marginTop: '0.4rem', marginBottom: '1rem',
      }}>
        Signing in as <span style={{ color: 'var(--white)' }}>{email}</span>
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ position: 'relative' }}>
          <Input
            ref={passwordRef}
            data-testid="auth-signin-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 44, paddingRight: 38 }}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 10, top: 0, bottom: 0,
              display: 'flex', alignItems: 'center',
              background: 'none', border: 'none', color: 'var(--ts)', cursor: 'pointer',
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
          <Link
            to="/login"
            data-testid="auth-forgot-password"
            style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem',
              color: 'var(--a)',
            }}
          >
            Forgot password?
          </Link>
        </div>

        {totpRequired && (
          <Input
            data-testid="auth-signin-totp"
            placeholder="6-digit code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="rounded-[3px]"
            style={{ ...inputStyle, height: 42, marginTop: '0.6rem' }}
          />
        )}

        {error && (
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem',
            color: 'var(--danger)', marginTop: '0.75rem',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}>
            <AlertCircle size={13} /> {error}
          </p>
        )}

        <Button
          type="submit"
          data-testid="auth-signin-submit"
          disabled={submitting}
          className="btn-shimmer"
          style={{ ...primaryButtonStyle, marginTop: '1rem', opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? (
            <span className="inline-flex items-center" style={{ gap: '0.5rem' }}>
              <Loader2 size={14} style={{ animation: 'scannerSpin 1s linear infinite' }} />
              Signing in...
            </span>
          ) : (
            <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
              Sign In <ArrowRight size={14} />
            </span>
          )}
        </Button>
      </form>
    </div>
  );
}

/* ───────────────────── STATE 4 — Welcome back ───────────────────── */

function WelcomeBackCard({ authFetch, user, onContinue }) {
  const [subscription, setSubscription] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(3);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/billing/subscription`);
        if (res.ok && !cancelled) {
          setSubscription(await res.json());
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onContinue();
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, onContinue]);

  const firstName = user?.first_name || user?.email?.split('@')[0] || 'there';
  const planName = subscription?.plan
    ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)
    : '';
  const status = subscription?.status;
  let statusLine = subscription ? `Acodia Watch ${planName}` : '';
  let statusColor = 'var(--ts)';
  if (status === 'trial') {
    const ends = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
    const days = ends
      ? Math.max(0, Math.ceil((ends.getTime() - Date.now()) / 86400000))
      : null;
    statusLine = `Trial — ${days ?? '—'} day${days === 1 ? '' : 's'} remaining`;
    statusColor = 'var(--warn)';
  } else if (status === 'active') {
    statusLine = 'Active subscription';
    statusColor = 'var(--ok)';
  }

  return (
    <div data-testid="auth-welcome-back" style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(74,222,128,0.12)',
            border: '1px solid rgba(74,222,128,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Check size={28} style={{ color: 'var(--ok)' }} />
        </div>
      </div>

      <h1 style={{
        fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: '1.2rem',
        color: 'var(--white)', textAlign: 'center',
      }}>
        Welcome back, {firstName}
      </h1>

      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
        color: 'var(--ts)', textAlign: 'center', marginTop: '0.6rem',
      }}>
        {subscription ? `Acodia Watch ${planName}` : 'Loading your account...'}
      </p>
      {status && (
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem',
          color: statusColor, textAlign: 'center', marginTop: '0.2rem',
        }}>
          {statusLine}
        </p>
      )}

      <Button
        type="button"
        data-testid="auth-welcome-continue"
        onClick={onContinue}
        className="btn-shimmer"
        style={{ ...primaryButtonStyle, marginTop: '1.5rem' }}
      >
        <span className="inline-flex items-center" style={{ gap: '0.4rem' }}>
          View My Account <ArrowRight size={14} />
        </span>
      </Button>

      <p style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem',
        color: 'var(--ts)', textAlign: 'center', marginTop: '0.75rem',
      }}>
        You will be redirected automatically in {secondsLeft} second{secondsLeft === 1 ? '' : 's'}
      </p>
    </div>
  );
}

/* ── Shared back arrow ── */
function BackButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="auth-back-button"
      aria-label="Back"
      style={{
        position: 'absolute', top: 16, left: 16,
        background: 'none', border: 'none', color: 'var(--ts)',
        cursor: 'pointer', padding: 6, lineHeight: 0,
      }}
    >
      <ArrowLeft size={18} />
    </button>
  );
}
